import { useEffect, useState } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    'Content-Type': 'application/json'
  };
}

export default function StudentProfile({
  session,
  onBack,
  onSaved
}) {
  const [englishName, setEnglishName] = useState('');
  const [chineseName, setChineseName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setEnglishName(session?.englishName || '');
    setChineseName(session?.chineseName || '');
    setEmail(session?.email || '');
    setPassword('');
    setError('');
    setSuccess('');
  }, [session]);

  async function handleSubmit(event) {
    event.preventDefault();

    const nextEmail = email.trim().toLowerCase();

    if (!nextEmail || !nextEmail.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }

    if (!password) {
      setError('Enter your current password to save these changes.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/student_update_own_profile`,
        {
          method: 'POST',
          headers: apiHeaders(),
          body: JSON.stringify({
            p_student_id: session.studentId,
            p_password: password,
            p_english_name: englishName,
            p_chinese_name: chineseName,
            p_email: nextEmail
          })
        }
      );

      const text = await response.text();
      const result = text ? JSON.parse(text) : null;

      if (!response.ok) {
        throw new Error(
          `Saving profile failed (${response.status}): ${text || response.statusText}`
        );
      }

      if (!result?.ok) {
        if (result?.status === 'invalid_password') {
          throw new Error('The current password is incorrect.');
        }

        if (result?.status === 'invalid_email') {
          throw new Error('Enter a valid email address.');
        }

        throw new Error('Your profile could not be updated.');
      }

      const student = result.student || {};
      const nextSession = {
        ...session,
        englishName: student.english_name || '',
        chineseName: student.chinese_name || '',
        email: student.email || nextEmail
      };

      setEnglishName(nextSession.englishName);
      setChineseName(nextSession.chineseName);
      setEmail(nextSession.email);
      setPassword('');
      setSuccess('Profile updated.');

      if (onSaved) {
        onSaved(nextSession);
      }
    } catch (err) {
      setError(err?.message || 'Your profile could not be updated.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.heading}>Profile</h2>
            <div style={styles.subheading}>
              Update your name or email address.
            </div>
          </div>

          {onBack && (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={onBack}
            >
              Back
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>
            Student ID
            <input
              type="text"
              value={session?.studentCode || ''}
              readOnly
              style={{
                ...styles.input,
                ...styles.readOnlyInput
              }}
            />
          </label>

          <label style={styles.label}>
            English name
            <input
              type="text"
              value={englishName}
              onChange={event =>
                setEnglishName(event.target.value)
              }
              style={styles.input}
              autoComplete="name"
            />
          </label>

          <label style={styles.label}>
            Chinese name
            <input
              type="text"
              value={chineseName}
              onChange={event =>
                setChineseName(event.target.value)
              }
              style={styles.input}
            />
          </label>

          <label style={styles.label}>
            Email address
            <input
              type="email"
              value={email}
              onChange={event =>
                setEmail(event.target.value)
              }
              style={styles.input}
              autoComplete="email"
              required
            />
          </label>

          <label style={styles.label}>
            Current password
            <input
              type="password"
              value={password}
              onChange={event =>
                setPassword(event.target.value)
              }
              style={styles.input}
              autoComplete="current-password"
              required
            />
            <span style={styles.help}>
              Required only to confirm that you are changing your own account.
            </span>
          </label>

          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {success && (
            <div style={styles.success}>
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.65 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    width: '100%',
    minHeight: '100%',
    boxSizing: 'border-box',
    padding: '22px'
  },
  card: {
    width: '100%',
    maxWidth: '620px',
    margin: '0 auto',
    background: '#ffffff',
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '22px',
    boxSizing: 'border-box'
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '14px',
    marginBottom: '20px'
  },
  heading: {
    margin: 0,
    color: '#102a43'
  },
  subheading: {
    marginTop: '5px',
    color: '#66788a'
  },
  label: {
    display: 'block',
    color: '#334e68',
    fontWeight: 700,
    marginBottom: '15px'
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '6px',
    padding: '10px 11px',
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    font: 'inherit',
    color: '#102a43',
    background: '#ffffff'
  },
  readOnlyInput: {
    background: '#f4f6f8',
    color: '#7b8794',
    cursor: 'not-allowed'
  },
  help: {
    display: 'block',
    marginTop: '5px',
    color: '#829ab1',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.4
  },
  primaryButton: {
    width: '100%',
    border: 0,
    borderRadius: '6px',
    padding: '11px 14px',
    background: '#215b7d',
    color: '#ffffff',
    fontWeight: 800,
    cursor: 'pointer'
  },
  secondaryButton: {
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    padding: '8px 12px',
    background: '#ffffff',
    color: '#334e68',
    fontWeight: 700,
    cursor: 'pointer'
  },
  error: {
    marginBottom: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    background: '#fff1f0',
    color: '#a61b1b'
  },
  success: {
    marginBottom: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    background: '#edf7ed',
    color: '#276738'
  }
};
